import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper, useReactTable, getCoreRowModel } from '@tanstack/react-table';
import Table from '../components/Table';
import { useBlueprintEvalsAuth } from '../hooks/useBlueprintEvalsAuth';
import './Quotes.css';

// Secret, unlinked page (see the /evals route in App.tsx) for
// reviewing blueprint_ai_session/turn rows and flagging bad AI generations.
// Not gated by the app's normal login — see useBlueprintEvalsAuth.

const API_HOST = process.env.REACT_APP_API_HOST || 'http://localhost:8080';

type ThumbsFilter = 'all' | 'true' | 'false';

interface Turn {
  id: number;
  sessionId: number;
  index: number;
  promptInput: string;
  modelId: string;
  inputTokens: number | null;
  outputTokens: number | null;
  latencyMs: number | null;
  thumbsDownBool: boolean;
  createdAt: string;
  domainName: string;
  sessionTurnCount: number;
}

const columnHelper = createColumnHelper<Turn>();

export default function BlueprintEvals() {
  const navigate = useNavigate();
  const { password, setPassword, authHeaders } = useBlueprintEvalsAuth();
  const [passwordInput, setPasswordInput] = useState('');
  const [filter, setFilter] = useState<ThumbsFilter>('all');
  const [multiTurnOnly, setMultiTurnOnly] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const loadTurns = async (pw: string, f: ThumbsFilter) => {
    setLoading(true);
    setError('');
    try {
      const qs = f === 'all' ? '' : `?thumbs_down=${f}`;
      const res = await fetch(`${API_HOST}/api/blueprint-evals/turns${qs}`, {
        headers: authHeaders(pw),
      });
      if (res.status === 401) {
        setPassword('');
        setError('Incorrect password.');
        setTurns([]);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setTurns(data.turns);
    } catch (err: any) {
      setError(err.message || 'Failed to load turns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (password) loadTurns(password, filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, filter]);

  const handleUnlock = (e: React.FormEvent) => {
    e.preventDefault();
    setPassword(passwordInput);
    setPasswordInput('');
  };

  const handleToggle = async (turn: Turn) => {
    setUpdatingId(turn.id);
    try {
      const res = await fetch(`${API_HOST}/api/blueprint-evals/turns/${turn.id}/toggle-thumbs-down`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to toggle flag');
      }
      await loadTurns(password, filter);
    } catch (err: any) {
      setError(err.message || 'Failed to toggle flag');
    } finally {
      setUpdatingId(null);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('sessionId', {
        header: 'Session',
        cell: (info) => {
          const t = info.row.original;
          return (
            <span
              style={{ color: '#3b82f6', fontWeight: 600, cursor: 'pointer' }}
              onClick={() => navigate(`/evals/session/${t.sessionId}`)}
            >
              #{t.sessionId}
              {t.sessionTurnCount > 1 ? ` (${t.sessionTurnCount} turns)` : ''}
            </span>
          );
        },
        size: 140,
      }),
      columnHelper.accessor('index', { header: 'Turn #', size: 60 }),
      columnHelper.accessor('domainName', { header: 'Domain', size: 120 }),
      columnHelper.accessor('promptInput', {
        header: 'Prompt',
        cell: (info) => {
          const v = info.getValue();
          return <span title={v}>{v.length > 100 ? `${v.slice(0, 100)}…` : v}</span>;
        },
        size: 340,
      }),
      columnHelper.accessor('modelId', { header: 'Model', size: 160 }),
      columnHelper.accessor((row) => `${row.inputTokens ?? '—'} / ${row.outputTokens ?? '—'}`, {
        id: 'tokens',
        header: 'Tokens in/out',
        size: 100,
      }),
      columnHelper.accessor('latencyMs', {
        header: 'Latency',
        cell: (info) => (info.getValue() != null ? `${info.getValue()} ms` : '—'),
        size: 90,
      }),
      columnHelper.accessor('thumbsDownBool', {
        header: 'Status',
        cell: (info) => (
          <span
            className="status-badge"
            style={
              info.getValue()
                ? { backgroundColor: '#fee2e2', color: '#991b1b' }
                : { backgroundColor: '#d1fae5', color: '#065f46' }
            }
          >
            {info.getValue() ? 'Flagged' : 'OK'}
          </span>
        ),
        size: 90,
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created',
        cell: (info) => new Date(info.getValue()).toLocaleString(),
        size: 160,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Action',
        cell: (info) => {
          const t = info.row.original;
          return (
            <button
              disabled={updatingId === t.id}
              onClick={() => handleToggle(t)}
              style={{
                padding: '4px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                background: '#fff',
                color: '#374151',
                cursor: 'pointer',
              }}
            >
              {t.thumbsDownBool ? 'Clear' : 'Flag 👎'}
            </button>
          );
        },
        size: 90,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, updatingId]
  );

  // sessionTurnCount already comes back on every turn row from the API, so
  // this filter is just a client-side narrow — no extra request needed.
  const visibleTurns = useMemo(
    () => (multiTurnOnly ? turns.filter((t) => t.sessionTurnCount > 1) : turns),
    [turns, multiTurnOnly]
  );

  const table = useReactTable({
    data: visibleTurns,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!password) {
    return (
      <div className="quotes-page">
        <div className="page-header">
          <div>
            <h1>Blueprint AI Evals</h1>
            <p className="subtitle">Enter the eval password to continue</p>
          </div>
        </div>
        <div className="page-content">
          <form onSubmit={handleUnlock} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <input
              type="password"
              className="filter-input"
              placeholder="Password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              autoFocus
            />
            <button type="submit" className="primary-button">Unlock</button>
          </form>
          {error && (
            <div className="error-message" style={{ marginTop: 12 }}>
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="quotes-page">
      <div className="page-header">
        <div>
          <h1>Blueprint AI Evals</h1>
          <p className="subtitle">Review and flag /ai/blueprint prompt/response turns</p>
        </div>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="filter-bar" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {(['all', 'true', 'false'] as ThumbsFilter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={f === filter ? 'primary-button' : undefined}
              style={
                f === filter
                  ? undefined
                  : { padding: '9px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', color: '#374151', cursor: 'pointer' }
              }
            >
              {f === 'all' ? 'All' : f === 'true' ? 'Flagged only' : 'Unflagged only'}
            </button>
          ))}
          <button
            onClick={() => setMultiTurnOnly((v) => !v)}
            className={multiTurnOnly ? 'primary-button' : undefined}
            style={
              multiTurnOnly
                ? undefined
                : { padding: '9px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', color: '#374151', cursor: 'pointer' }
            }
          >
            &gt;1 turns
          </button>
        </div>

        <Table table={table} isLoading={loading} />
      </div>
    </div>
  );
}
